import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import TextField from '@mui/material/TextField';
import { DropzoneAreaBase } from 'material-ui-dropzone';
import { NON_ERASABLE_SECTION_ID, ENDPOINT, LIMIT_PAGE } from 'constants/variables';
import { successErrorAction, warningErrorAction } from 'actions/ErrorActions';
import { helpHttp } from 'service/HelpHttp';
import { setLoading } from 'reducers/loadingSlice';
import TableSortingSection from 'pages/commonsarticle/departmentSettings/SortingTablesSection';
import SubSectionSettings from 'pages/commonsarticle/departmentSettings/SubSectiionSettings';
import 'css/generalSettings.css';
import 'css/customDialog.css';

const INITIAL_FILTERS = {
  idFrom: '',
  idTo: '',
  codeFrom: '',
  codeTo: '',
  nameesFrom: '',
  nameesTo: '',
  nameenFrom: '',
  nameenTo: '' 
};

const EMPTY_SECTION = {
  id: '',
  code: '',
  namees: '',
  nameen: '',
  filename: '',
  createdate: null,
  datafile: '',
  type: '',
  numTotSubSections: 0,
  subsections: [],
};

const INITIAL_DATA = [EMPTY_SECTION];

const HEAD_CELLS = [
  { id: 'id', numeric: false, disablePadding: false, label: 'Text_Code', canOrder: true },
  { id: 'code', numeric: false, disablePadding: false, label: 'Text_CodeSection', canOrder: true },
  { id: 'namees', numeric: false, disablePadding: false, label: 'Text_NameEs', canOrder: true },
  { id: 'nameen', numeric: false, disablePadding: false, label: 'Text_NameEn', canOrder: true },
  { id: 'image', numeric: false, disablePadding: false, label: 'TextImage', canOrder: false },
  { id: 'subsections', numeric: false, disablePadding: false, label: 'Text_Sections', canOrder: false },
];

const INVALID_CHARACTERS_REGEX = /[!$%&|<>?{}~"']+/;
const NUMBER_REGEX = /^\d+$/;
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const normalizeString = (value) => String(value || '').trim().toLowerCase();
const buildOffsetRanges = (total) => {
  if (total <= LIMIT_PAGE) {
    return [{ value: 0, label: `1 - ${Number(total || LIMIT_PAGE).toLocaleString('de-DE')}` }];
  }
  const ranges = [];
  for (let offset = 0; offset < total; offset += Number(LIMIT_PAGE)) {
    const from = offset + 1;
    const to = Math.min(offset + Number(LIMIT_PAGE), total);
    ranges.push({
      value: offset,
      label: `${from.toLocaleString('de-DE')} - ${to.toLocaleString('de-DE')}`,
    });
  }
  return ranges;
};

const sectionToFileObject = async (section) => {
  if (!section?.filename || !section?.datafile || !section?.type) {
    return [];
  }
  const dataUrl = `data:${section.type};base64,${section.datafile}`;
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], section.filename, {
    type: section.type,
    lastModified: section.createdate ? new Date(section.createdate).getTime() : Date.now(),
  });
  return [{ data: dataUrl, file }];
};

const SectionSettings = ({ openSections, setOpenSections, rowEditDepartment }) => {
  const { t, i18n } = useTranslation(['listDataArticles']);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const roles = useSelector((state) => state.loginUser.roles);
  const deleteCode = useSelector((state) => state.formatDIUser.delete_code);
  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const [dataSection, setDataSection] = useState(INITIAL_DATA);
  const [formSection, setFormSection] = useState(EMPTY_SECTION);
  const [rowEditSection, setRowEditSection] = useState(EMPTY_SECTION);
  const [filtersSection, setFiltersSection] = useState(INITIAL_FILTERS);
  const [applyFiltersSection, setApplyFiltersSection] = useState(INITIAL_FILTERS);
  const [numTotRegSection, setNumTotRegSection] = useState(0);
  const [numTotRegSectionByDepartment, setNumTotRegSectionByDepartment] = useState(0);
  const [numTotRegFilter, setNumTotRegFilter] = useState(0);
  const [errorsFiltersSection, setErrorsFiltersSection] = useState({});
  const [errorsSection, setErrorsSection] = useState({});
  const [inputTextSection, setInputTextSection] = useState('');
  const [files, setFiles] = useState([]);
  const [openCreateDataSection, setOpenCreateDataSection] = useState(false);
  const [openDeleteRowSection, setOpenDeleteRowSection] = useState(false);
  const [openEditRowSection, setOpenEditRowSection] = useState(false);
  const [openEditFilterSection, setOpenEditFilterSection] = useState(false);
  const [openSubSection, setOpenSubSection] = useState(false);
  const [isLoadingErrorSection, setIsLoadingErrorSection] = useState(false);
  const [idCodeDelete, setIdCodeDelete] = useState(deleteCode);
  const [activeOffset, setActiveOffset] = useState(false);
  const [currencyOffset, setCurrencyOffset] = useState(0);
  const [rangeOffset, setRangeOffset] = useState([{}]);
  const [refreshData, setRefreshData] = useState(false);

  const permissions = useMemo(() => {
    const menus = roles?.[0]?.listmenus || [];
    const hasPermission = (name) => menus.some((menu) => menu.name === name);

    return {
      canCreate: hasPermission('SubMenu_Section_Create'),
      canEdit: hasPermission('SubMenu_Section_Edit'),
      canDelete: hasPermission('SubMenu_Section_Delete'),
      canViewSubSections: hasPermission('SubMenu_SubSettings_Section'),
    };
  }, [roles]);

  const resetLocalState = useCallback(() => {
    setDataSection(INITIAL_DATA);
    setFormSection(EMPTY_SECTION);
    setRowEditSection(EMPTY_SECTION);
    setFiltersSection(INITIAL_FILTERS);
    setApplyFiltersSection(INITIAL_FILTERS);
    setNumTotRegSection(0);
    setNumTotRegSectionByDepartment(0);
    setNumTotRegFilter(0);
    setErrorsFiltersSection({});
    setErrorsSection({});
    setInputTextSection('');
    setOpenCreateDataSection(false);
    setOpenDeleteRowSection(false);
    setOpenEditRowSection(false);
    setOpenEditFilterSection(false);
    setOpenSubSection(false);
    setIsLoadingErrorSection(false);
    setIdCodeDelete(deleteCode);
    setActiveOffset(false);
    setCurrencyOffset(0);
    setRangeOffset([{}]);
    setRefreshData(false);
    setFiles([]);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const closeModalSectionSettings = useCallback(() => {
    resetLocalState();
    setOpenSections(false);
  }, [resetLocalState, setOpenSections]);

  const validateFilterForm = useCallback(() => {
    const errors = {};

    if (hasValue(filtersSection.idFrom) && !NUMBER_REGEX.test(filtersSection.idFrom)) {
      errors.idFrom = 'Text_TextNoNumber';
    }
    if (hasValue(filtersSection.idTo) && !NUMBER_REGEX.test(filtersSection.idTo)) {
      errors.idTo = 'Text_TextNoNumber';
    }
    if (
      hasValue(filtersSection.idFrom) &&
      hasValue(filtersSection.idTo) &&
      !errors.idFrom &&
      !errors.idTo &&
      Number(filtersSection.idTo) < Number(filtersSection.idFrom)
    ) {
      errors.idTo = 'Text_TextGreater_Id';
    }

    ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
      if (INVALID_CHARACTERS_REGEX.test(filtersSection[field])) {
        errors[field] = 'Text_TextNoValid';
      }
    });

    if (hasValue(filtersSection.codeTo) && filtersSection.codeTo < filtersSection.codeFrom) {
      errors.codeTo = 'Text_TextGreater_SectionCode';
    }
    if (hasValue(filtersSection.nameesTo) && filtersSection.nameesTo < filtersSection.nameesFrom) {
      errors.nameesTo = 'Text_TextGreater_Namees';
    }
    if (hasValue(filtersSection.nameenTo) && filtersSection.nameenTo < filtersSection.nameenFrom) {
      errors.nameenTo = 'Text_TextGreater_Nameen';
    }

    return errors;
  }, [filtersSection]);

  const validateSectionForm = useCallback(() => {
    const errors = {};
    const currentId = rowEditSection.id;

    const codeExists = dataSection.some(
      (item) => normalizeString(item.code) === normalizeString(formSection.code) && (!openEditRowSection || item.id !== currentId),
    );
    const nameEsExists = dataSection.some(
      (item) => normalizeString(item.namees) === normalizeString(formSection.namees) && (!openEditRowSection || item.id !== currentId),
    );
    const nameEnExists = dataSection.some(
      (item) => normalizeString(item.nameen) === normalizeString(formSection.nameen) && (!openEditRowSection || item.id !== currentId),
    );

    if (!hasValue(formSection.code)) {
      errors.code = 'Text_FieldCodeSectionRequired';
    } else if (codeExists) {
      errors.code = 'Text_Section_CodeExist';
    }

    if (!hasValue(formSection.namees)) {
      errors.namees = 'Text_FieldNameesRequired';
    } else if (INVALID_CHARACTERS_REGEX.test(formSection.namees)) {
      errors.namees = 'Text_TextNoValid';
    } else if (nameEsExists) {
      errors.namees = 'Text_Section_NameesExist';
    }

    if (!hasValue(formSection.nameen)) {
      errors.nameen = 'Text_FieldNameenRequired';
    } else if (INVALID_CHARACTERS_REGEX.test(formSection.nameen)) {
      errors.nameen = 'Text_TextNoValid';
    } else if (nameEnExists) {
      errors.nameen = 'Text_Section_NameenExist';
    }

    return errors;
  }, [dataSection, formSection, openEditRowSection, rowEditSection.id]);

  const filteredDataSection = useMemo(() => {
    const search = normalizeString(inputTextSection);
    if (!search) {
      return dataSection;
    }

    return dataSection.filter((item) => {
      const searchable = [item.id, item.code, item.namees, item.nameen].map((value) => String(value || '')).join(' ').toLowerCase();
      return searchable.includes(search);
    });
  }, [dataSection, inputTextSection]);

  const openCreateOrEditDataSection = useCallback(() => {
    setInputTextSection('');
    setErrorsSection({});
    setFormSection(EMPTY_SECTION);
    setFiles([]);
    setOpenEditRowSection(false);
    setOpenCreateDataSection(true);
  }, []);

  const openEditFiltersSection = useCallback(() => {
    setFiltersSection(applyFiltersSection);
    setErrorsFiltersSection({});
    setOpenEditFilterSection((prev) => !prev);
  }, [applyFiltersSection]);

  const handleChangeFilterEditSection = useCallback((event) => {
    const { name, value } = event.target;
    setFiltersSection((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurFilterEditSection = useCallback(
    (event) => {
      const { name, value } = event.target;
      const nextFilters = { ...filtersSection, [name]: value };
      setFiltersSection(nextFilters);

      const errors = {};
      if (hasValue(nextFilters.idFrom) && !NUMBER_REGEX.test(nextFilters.idFrom)) {
        errors.idFrom = 'Text_TextNoNumber';
      }
      if (hasValue(nextFilters.idTo) && !NUMBER_REGEX.test(nextFilters.idTo)) {
        errors.idTo = 'Text_TextNoNumber';
      }
      if (
        hasValue(nextFilters.idFrom) &&
        hasValue(nextFilters.idTo) &&
        !errors.idFrom &&
        !errors.idTo &&
        Number(nextFilters.idTo) < Number(nextFilters.idFrom)
      ) {
        errors.idTo = 'Text_TextGreater_Id';
      }
      ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
        if (INVALID_CHARACTERS_REGEX.test(nextFilters[field])) {
          errors[field] = 'Text_TextNoValid';
        }
      });
      if (hasValue(nextFilters.codeTo) && nextFilters.codeTo < nextFilters.codeFrom) {
        errors.codeTo = 'Text_TextGreater_SectionCode';
      }
      if (hasValue(nextFilters.nameesTo) && nextFilters.nameesTo < nextFilters.nameesFrom) {
        errors.nameesTo = 'Text_TextGreater_Namees';
      }
      if (hasValue(nextFilters.nameenTo) && nextFilters.nameenTo < nextFilters.nameenFrom) {
        errors.nameenTo = 'Text_TextGreater_Nameen';
      }

      setErrorsFiltersSection(errors);
    },
    [filtersSection],
  );

  const handleSubmitFilterEditSection = useCallback(() => {
    const errors = validateFilterForm();
    setErrorsFiltersSection(errors);

    if (Object.keys(errors).length > 0) {
      dispatch(warningErrorAction(t('Text_Error_Filter')));
      return;
    }

    setApplyFiltersSection(filtersSection);
    setCurrencyOffset(0);
    setErrorsFiltersSection({});
    setOpenEditFilterSection(false);
  }, [dispatch, filtersSection, t, validateFilterForm]);

  const handleDeleteFiltersSection = useCallback(() => {
    setErrorsFiltersSection({});
    setFiltersSection(INITIAL_FILTERS);
    setApplyFiltersSection(INITIAL_FILTERS);
    setCurrencyOffset(0);
    setOpenEditFilterSection(false);
  }, []);

  const toggleDrawerFilterEditSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        setErrorsFiltersSection({});
        setFiltersSection(INITIAL_FILTERS);
        setOpenEditFilterSection(false);
      }
    },
    [],
  );

  const handleChangeDataEditSection = useCallback((event) => {
    const { name, value } = event.target;
    setFormSection((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDataEditSection = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormSection((prev) => {
        const updated = { ...prev, [name]: value };
        const currentId = rowEditSection.id;
        const errors = {};

        const codeExists = dataSection.some(
          (item) => normalizeString(item.code) === normalizeString(updated.code) && (!openEditRowSection || item.id !== currentId),
        );
        const nameEsExists = dataSection.some(
          (item) => normalizeString(item.namees) === normalizeString(updated.namees) && (!openEditRowSection || item.id !== currentId),
        );
        const nameEnExists = dataSection.some(
          (item) => normalizeString(item.nameen) === normalizeString(updated.nameen) && (!openEditRowSection || item.id !== currentId),
        );

        if (!hasValue(updated.code)) {
          errors.code = 'Text_FieldCodeSectionRequired';
        } else if (codeExists) {
          errors.code = 'Text_Section_CodeExist';
        }

        if (!hasValue(updated.namees)) {
          errors.namees = 'Text_FieldNameesRequired';
        } else if (INVALID_CHARACTERS_REGEX.test(updated.namees)) {
          errors.namees = 'Text_TextNoValid';
        } else if (nameEsExists) {
          errors.namees = 'Text_Section_NameesExist';
        }

        if (!hasValue(updated.nameen)) {
          errors.nameen = 'Text_FieldNameenRequired';
        } else if (INVALID_CHARACTERS_REGEX.test(updated.nameen)) {
          errors.nameen = 'Text_TextNoValid';
        } else if (nameEnExists) {
          errors.nameen = 'Text_Section_NameenExist';
        }

        setErrorsSection(errors);
        return updated;
      });
    },
    [dataSection, openEditRowSection, rowEditSection.id],
  );

  const handleAddFileImage = useCallback((newFiles) => {
    setFiles([...newFiles]);
  }, []);

  const handleDeleteFileImage = useCallback((deletedFile) => {
    setFiles((prev) => prev.filter((file) => file !== deletedFile));
  }, []);

  const toBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      }),
    [],
  );

  const buildUpdatedFiles = useCallback(async () => {
    return Promise.all(
      files.map(async (item, index) => {
        const file = item.file || item;
        const base64 = await toBase64(file);
        return {
          id: index,
          filename: file.name,
          createdate: file.lastModified,
          datafile: base64,
          type: file.type,
        };
      }),
    );
  }, [files, toBase64]);

  const handleCloseDataEditSection = useCallback(() => {
    setOpenCreateDataSection(false);
    setOpenEditRowSection(false);
    setErrorsSection({});
    setFormSection(EMPTY_SECTION);
    setFiles([]);
    setRefreshData(false);
    setIsLoadingErrorSection(false);
    dispatch(setLoading(false));
  }, [dispatch]);

  const toggleDrawerDataEditSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        handleCloseDataEditSection();
      }
    },
    [handleCloseDataEditSection],
  );

  const toggleDrawerSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        closeModalSectionSettings();
      }
    },
    [closeModalSectionSettings],
  );

  const submitSectionRequest = useCallback(
    async (payload) => {
      dispatch(setLoading(true));
      try {
        const response = openEditRowSection
          ? await helpHttp().put2(ENDPOINT, 'section/updatesection', token, rowEditDepartment.id, payload)
          : await helpHttp().post2(ENDPOINT, 'section/savesection', token, rowEditDepartment.id, payload);

        if (response.status === 'OK') {
          dispatch(successErrorAction(t('Text_SaveData')));
          setRefreshData(true);
          handleCloseDataEditSection();
          return;
        }
        dispatch(warningErrorAction(t(response.cause)));
      } catch (error) {
        dispatch(warningErrorAction(t('Text_IncorrectData')));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, handleCloseDataEditSection, openEditRowSection, rowEditDepartment.id, t, token],
  );

  const handleSubmitDataEditSection = useCallback(async () => {
    const errors = validateSectionForm();
    setErrorsSection(errors);
    if (Object.keys(errors).length > 0) {
      if (errors.code) dispatch(warningErrorAction(t(errors.code)));
      else if (errors.namees) dispatch(warningErrorAction(t(errors.namees)));
      else if (errors.nameen) dispatch(warningErrorAction(t(errors.nameen)));
      else dispatch(warningErrorAction(t('Text_IncorrectData')));
      return;
    }
    const updatedFiles = files.length > 0 ? await buildUpdatedFiles() : [];
    const firstFile = updatedFiles[0];
    const payload = {
      ...formSection,
      filename: firstFile?.filename || '',
      createdate: firstFile?.createdate || null,
      datafile: firstFile?.datafile || '',
      type: firstFile?.type || '',
    };

    setFormSection(payload);
    await submitSectionRequest(payload);
  }, [buildUpdatedFiles, dispatch, files.length, formSection, submitSectionRequest, t, validateSectionForm]);

  const handleChangeDeleteRowSection = useCallback((event) => {
    setIdCodeDelete(event.target.value);
  }, []);

  const handleCancelDeleteRowSection = useCallback(() => {
    setErrorsSection({});
    setRowEditSection(EMPTY_SECTION);
    setFormSection(EMPTY_SECTION);
    setIdCodeDelete(deleteCode);
    setIsLoadingErrorSection(false);
    setOpenDeleteRowSection(false);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const handleCloseDeleteRowSection = useCallback(async () => {
    if (idCodeDelete !== deleteCode) {
      dispatch(warningErrorAction(t('Text_InvalidCode')));
      return;
    }

    if (!rowEditSection.id) {
      dispatch(warningErrorAction(t('Text_InvalidId')));
      return;
    }

    dispatch(setLoading(true));
    try {
      const response = await helpHttp().del(ENDPOINT, 'section/deletesection', token, rowEditSection);
      if (response.status === 'OK') {
        dispatch(successErrorAction(t('Text_ValidCode')));
        dispatch(successErrorAction(t('Text_DeleteField')));
        setRefreshData(true);
      } else {
        dispatch(warningErrorAction(t(response.cause)));
      }
    } catch (error) {
      dispatch(warningErrorAction(t('Text_IncorrectData')));
    } finally {
      dispatch(setLoading(false));
      setIdCodeDelete(deleteCode);
      setOpenDeleteRowSection(false);
      handleCloseDataEditSection();
    }
  }, [deleteCode, dispatch, handleCloseDataEditSection, idCodeDelete, rowEditSection, t, token]);

  const inputHandlerSection = useCallback((event) => {
    setInputTextSection(event.target.value.toLowerCase());
  }, []);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && openSections) {
        closeModalSectionSettings();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [closeModalSectionSettings, openSections]);

  useEffect(() => {
    setActiveOffset(numTotRegFilter > LIMIT_PAGE);
    setRangeOffset(numTotRegFilter > LIMIT_PAGE ? buildOffsetRanges(numTotRegFilter) : [{}]);
    if (numTotRegFilter <= LIMIT_PAGE) {
      setCurrencyOffset(0);
    }
  }, [numTotRegFilter]);

  useEffect(() => {
    let ignore = false;

    const fetchAll = async () => {
      if ((!openSections && !refreshData) || !rowEditDepartment?.id) {
        return;
      }
      dispatch(setLoading(true));
      try {
        const [totalResponse, totalByDepartmentResponse, filteredTotalResponse, dataResponse] = await Promise.all([
          helpHttp().get(ENDPOINT, 'section/getinformationtablesection', token),
          helpHttp().get2(ENDPOINT, 'section/getinformationtablesectionbydepartment/', token, rowEditDepartment.id),
          helpHttp().post2(ENDPOINT, 'section/getcountfiltersections', token, rowEditDepartment.id, applyFiltersSection),
          helpHttp().get4(ENDPOINT, 'section/getsections', token, rowEditDepartment.id, currencyOffset, LIMIT_PAGE, applyFiltersSection),
        ]);

        if (ignore) {
          return;
        }
        const hasConnectionError = (response) => response?.err || response?.message === 'Load failed';
        const hasAuthError = (response) => response?.err;
        if (
          hasConnectionError(totalResponse) ||
          hasConnectionError(totalByDepartmentResponse) ||
          hasConnectionError(filteredTotalResponse) ||
          hasConnectionError(dataResponse)
        ) {
          setIsLoadingErrorSection(true);
          setDataSection(INITIAL_DATA);
          dispatch(
            warningErrorAction(
              t(dataResponse?.message === 'Load failed' ? 'Text_ConnectionFailied' : hasAuthError(dataResponse) ? 'Text_InvalidCredentials' : 'Text_IncorrectData'),
            ),
          );
          return;
        }
        setNumTotRegSection(totalResponse);
        setNumTotRegSectionByDepartment(totalByDepartmentResponse);
        setNumTotRegFilter(filteredTotalResponse);
        setDataSection(dataResponse);
        setIsLoadingErrorSection(false);
      } catch (error) {
        if (!ignore) {
          setIsLoadingErrorSection(true);
          setDataSection(INITIAL_DATA);
          dispatch(warningErrorAction(t('Text_IncorrectData')));
        }
      } finally {
        if (!ignore) {
          dispatch(setLoading(false));
          setRefreshData(false);
        }
      }
    };

    fetchAll();

    return () => {
      ignore = true;
    };
  }, [applyFiltersSection, currencyOffset, dispatch, openSections, refreshData, rowEditDepartment?.id, t, token]);

  useEffect(() => {
    let cancelled = false;

    const syncEditForm = async () => {
      if (!openEditRowSection) {
        setFormSection(EMPTY_SECTION);
        setFiles([]);
        return;
      }
      setFormSection({
        id: rowEditSection.id ?? '',
        code: rowEditSection.code ?? '',
        namees: rowEditSection.namees ?? '',
        nameen: rowEditSection.nameen ?? '',
        filename: rowEditSection.filename ?? '',
        createdate: rowEditSection.createdate ?? null,
        datafile: rowEditSection.datafile ?? '',
        type: rowEditSection.type ?? '',
        numTotSubSections: rowEditSection.numTotSubSections ?? rowEditSection.numTotSections ?? 0,
        subsections: rowEditSection.subsections ?? rowEditSection.sections ?? [],
      });

      const mappedFiles = await sectionToFileObject(rowEditSection);
      if (!cancelled) {
        setFiles(mappedFiles);
      }
    };

    syncEditForm();

    return () => {
      cancelled = true;
    };
  }, [openEditRowSection, rowEditSection]);

  if (!openSections) {
    return null;
  }

  return (
    <div>
      <SwipeableDrawer anchor="right" open onClose={toggleDrawerSection(false)} onOpen={toggleDrawerSection(true)}>
        <Container>
          <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '1050px' }}>
            <br />
            <div className="formdi-pending-header2">
              <h1 className="formdi-pending-title">
                {isLoadingErrorSection ? (
                  <i className="fas fa-solid fa-exclamation fa-2x text-danger" />
                ) : isLoadingData ? (
                  <CircularProgress color={openDeleteRowSection ? 'warning' : 'primary'} />
                ) : (
                  <i className="fas fa-solid fa-store text-warning" />
                )}{' '}
                {t('Text_Section_Config')}
              </h1>

              <div>
                {permissions.canCreate && (
                  <button
                    type="button"
                    className="formdi-pending-btn formdi-pending-btn--primary"
                    onClick={openCreateOrEditDataSection}
                  >
                    {t('Button_Create')}
                  </button>
                )}
                {permissions.canCreate && <span>&nbsp;&nbsp;</span>}
                <button
                  type="button"
                  className="formdi-pending-btn formdi-pending-btn--secondary"
                  onClick={closeModalSectionSettings}
                >
                  {t('Button_Back')}
                </button>
              </div>
            </div>

            <div id="myDIV">
              <h1 className="h1NewStyle mb-2">
                {i18n.language === 'es' ? rowEditDepartment.namees : rowEditDepartment.nameen}
              </h1>
            </div>

            <div className="formdi-pending-search">
              <TextField
                id="outlined-basic"
                variant="outlined"
                value={inputTextSection}
                inputProps={{ style: { fontSize: '18px' } }}
                InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                placeholder={t('Text_Section_Search')}
                onChange={inputHandlerSection}
                fullWidth
                label={t('Text_Section_Search')}
              />
            </div>

            <br />

            <div id="myNewDIV" className="formdi-pending-filters">
              <h1 className="formdi-pending-filter-title">{t('Text_Filter')}</h1>
              <div className="formdi-pending-filters-row">
                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSection.idFrom || applyFiltersSection.idTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSection.idFrom || applyFiltersSection.idTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_Code')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSection.codeFrom || applyFiltersSection.codeTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSection.codeFrom || applyFiltersSection.codeTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_CodeSection')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSection.nameesFrom || applyFiltersSection.nameesTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSection.nameesFrom || applyFiltersSection.nameesTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_NameEs')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSection.nameenFrom || applyFiltersSection.nameenTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSection.nameenFrom || applyFiltersSection.nameenTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_NameEn')}
                </button>
              </div>
            </div>

            <br />
            <hr className="sidebar-divider d-none d-md-block" />

            <div className="formdi-pending-card-flat">
              <div className="formdi-pending-card-content">
                <TableSortingSection
                  dataSection={filteredDataSection}
                  headCellsSection={HEAD_CELLS}
                  numTotRegFilter={numTotRegFilter}
                  titleTable={t('Text_Section_Table')}
                  setOpenCreateDataSection={setOpenCreateDataSection}
                  setOpenDeleteRowSection={setOpenDeleteRowSection}
                  setOpenEditRowSection={setOpenEditRowSection}
                  setRowEditSection={setRowEditSection}
                  showEditButtonSection={permissions.canEdit}
                  showDeleteButtonSection={permissions.canDelete}
                  showVisibilityButton={permissions.canViewSubSections}
                  setOpenSubSection={setOpenSubSection}
                  numTotRegSection={numTotRegSection}
                  numTotRegSectionByDepartment={numTotRegSectionByDepartment}
                  activeOffset={activeOffset}
                  rangeOffset={rangeOffset}
                  currencyOffset={currencyOffset}
                  setCurrencyOffset={setCurrencyOffset}
                  minvalue={Number(NON_ERASABLE_SECTION_ID)}
                />
              </div>
            </div>
          </Box>
        </Container>
      </SwipeableDrawer>

      {openEditFilterSection && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerFilterEditSection(false)}
          onOpen={toggleDrawerFilterEditSection(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">{t('Text_Filter')}</h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createfilter" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  {[
                    ['idFrom', 'Text_Filter_Code_From', 'PlaceHolder_Filter_Code_From', 15],
                    ['idTo', 'Text_Filter_Code_To', 'PlaceHolder_Filter_Code_to', 15],
                    ['codeFrom', 'Text_Filter_Section__CodeFrom', 'PlaceHolderr_Section_CodeFrom', 100],
                    ['codeTo', 'Text_Filter_Section__CodeTo', 'PlaceHolder_Section_CodeTo', 100],
                    ['nameesFrom', 'Text_Filter_NameEs_From', 'PlaceHolder_Filter_NameEs_From', 100],
                    ['nameesTo', 'PlaceHolder_Filter_NameEs_to', 'PlaceHolder_Filter_NameEs_to', 100],
                    ['nameenFrom', 'Text_Filter_NameEn_From', 'PlaceHolder_Filter_NameEn_From', 100],
                    ['nameenTo', 'Text_Filter_NameEn_To', 'PlaceHolder_Filter_NameEn_to', 100],
                  ].map(([name, labelKey, placeholderKey, maxLength]) => (
                    <React.Fragment key={name}>
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t(labelKey)} :
                      </label>
                      <div className="form-group">
                        <TextField
                          id={name}
                          name={name}
                          value={filtersSection[name]}
                          inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength }}
                          InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                          label={errorsFiltersSection[name] ? t(errorsFiltersSection[name]) : t(placeholderKey)}
                          placeholder={t(placeholderKey)}
                          variant="standard"
                          onBlur={handleBlurFilterEditSection}
                          onChange={handleChangeFilterEditSection}
                        />
                      </div>
                    </React.Fragment>
                  ))}

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitFilterEditSection}
                    >
                      {t('Button_Apply')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleDeleteFiltersSection}
                    >
                      {t('Button_Cancel_Filters')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      {(openCreateDataSection || openEditRowSection) && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerDataEditSection(false)}
          onOpen={toggleDrawerDataEditSection(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">
                  {openEditRowSection ? t('Text_Section_Edit') : t('Text_Section_Create')}
                </h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createcountry" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_Code')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="id"
                      name="id"
                      value={formSection.id}
                      inputProps={{ style: { width: 200, fontSize: '18px' }, maxLength: 15 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={t('PlaceHolder_Section_Code')}
                      placeholder={t('PlaceHolder_Section_Code')}
                      variant="standard"
                      disabled
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_CodeSection')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="code"
                      name="code"
                      value={formSection.code}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 10 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSection.code ? t(errorsSection.code) : t('Placeholder_CodeSection')}
                      placeholder={t('Placeholder_CodeSection')}
                      variant="standard"
                      onBlur={handleBlurDataEditSection}
                      onChange={handleChangeDataEditSection}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEs')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="namees"
                      name="namees"
                      value={formSection.namees}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSection.namees ? t(errorsSection.namees) : t('PlaceHolder_Section_NameEs')}
                      placeholder={t('PlaceHolder_Section_NameEs')}
                      variant="standard"
                      onBlur={handleBlurDataEditSection}
                      onChange={handleChangeDataEditSection}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEn')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="nameen"
                      name="nameen"
                      value={formSection.nameen}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSection.nameen ? t(errorsSection.nameen) : t('PlaceHolder_Section_NameEn')}
                      placeholder={t('PlaceHolder_Section_NameEn')}
                      variant="standard"
                      onBlur={handleBlurDataEditSection}
                      onChange={handleChangeDataEditSection}
                    />
                  </div>

                  <br />
                  <br />

                  <div className="upload-box" style={{ width: '100%' }}>
                    <DropzoneAreaBase
                      initialFiles={files}
                      fileObjects={files}
                      acceptedFiles={['image/*']}
                      maxFileSize={20000000}
                      filesLimit={1}
                      onAdd={handleAddFileImage}
                      onDelete={handleDeleteFileImage}
                      showAlerts={false}
                      dropzoneText={t('Text_Select_Image')}
                      showPreviews={false}
                      showPreviewsInDropzone={false}
                      showFileNamesInPreview={false}
                      showFileNames={false}
                      useChipsForPreview
                      previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
                      previewChipProps={{ classes: { root: 'upload-preview-chip' } }}
                      classes={{
                        root: 'upload-dropzone-root',
                        icon: 'upload-dropzone-icon',
                        text: 'upload-dropzone-text',
                      }}
                    />

                    {files.length > 0 && (
                      <div className="preview-thumbnails-wrapper">
                        <div className="preview-thumbnails">
                          {files.map((fileObj, index) => (
                            <div key={index} className="thumbnail-container">
                              <img src={fileObj.data} alt={`preview-${index}`} className="thumbnail-image" />
                              <button
                                type="button"
                                onClick={() => handleDeleteFileImage(fileObj)}
                                className="thumbnail-delete-btn"
                                title={t('Text_Delete')}
                              >
                                &times;
                              </button>
                              &nbsp;&nbsp;
                              <Chip
                                value={fileObj.file.name}
                                label={fileObj.file.name}
                                clickable={false}
                                style={{ color: '#e74a3b', fontSize: 14 }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitDataEditSection}
                    >
                      {openEditRowSection ? t('Button_Update') : t('Button_Create')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleCloseDataEditSection}
                    >
                      {t('Button_Cancel')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      <SubSectionSettings openSubSection={openSubSection} setOpenSubSection={setOpenSubSection} rowEditSection={rowEditSection} />

      <Dialog
        open={openDeleteRowSection}
        onClose={handleCancelDeleteRowSection}
        PaperProps={{ className: 'my-dialog-paper-danger' }}
      >
        <DialogTitle className="my-dialog-title-danger">
          {t('Text_Delete')} {rowEditSection.id} - {rowEditSection.code} -{' '}
          {i18n.language === 'es' ? rowEditSection.namees : rowEditSection.nameen}
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="my-dialog-contentText-danger">
            {t('Text_Line1')}
            <br />
            {t('Text_Line2')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="idCode"
            label={t('Text_Label')}
            type="text"
            fullWidth
            variant="standard"
            className="my-dialog-input-danger"
            value={idCodeDelete}
            onChange={handleChangeDeleteRowSection}
          />
        </DialogContent>
        <DialogActions className="my-dialog-actions">
          <Button onClick={handleCancelDeleteRowSection} className="my-dialog-button-cancel-danger">
            {t('Button_Cancel')}
          </Button>
          <Button variant="contained" onClick={handleCloseDeleteRowSection} className="my-dialog-button-delete">
            {t('Button_Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

SectionSettings.propTypes = {
  openSections: PropTypes.bool.isRequired,
  setOpenSections: PropTypes.func.isRequired,
  rowEditDepartment: PropTypes.object.isRequired,
};

export default SectionSettings;
